import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/useAppStore';
import { useBakingTimer } from '../../hooks/useBakingTimer';
import { useBakerActionsEnabled } from '../../hooks/useBakerActionsEnabled';
import { useMutationFeedback } from '../../hooks/useMutationFeedback';
import { getOrderDuration } from '../../utils/bakingTime';
import StickyBanner from '../ui/StickyBanner';
import ProgressBar from '../ui/ProgressBar';

function BakingTimerBanner({ order }) {
  const { t } = useTranslation();
  const enabled = useBakerActionsEnabled();
  const updateOrder = useAppStore((state) => state.updateOrder);
  const config = useAppStore((state) => state.config);
  const { run } = useMutationFeedback();

  const timer = useBakingTimer(order);

  if (!order) return null;

  const adjustBakingTime = async (delta) => {
    const currentDuration = getOrderDuration(order, config);
    const newDuration = Math.max(0, currentDuration + delta);
    await run(
      () => updateOrder(order.id, { bakingDuration: newDuration }),
      t('msg.bakingTime', { delta: `${delta > 0 ? '+' : ''}${delta}` }),
    );
  };

  return (
    <StickyBanner
      accentClass={timer.overtime ? 'border-l-red-500' : 'border-l-orange-500'}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`font-semibold ${timer.overtime ? 'text-red-400' : 'theme-accent-warm'}`}>
            {timer.overtime ? `🔥 ${t('banner.overtime')}` : `⏱ ${t('banner.baking')}`}
          </span>
          <span className={`font-semibold ${timer.overtime ? 'text-red-400' : 'theme-accent-warm'}`}>
            {timer.label}
          </span>
          <span className="text-sm theme-muted">
            — {order.orderNo ? `#${order.orderNo} ` : ''}{order.customerName}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[-30, -10, 10, 30].map((step) => (
            <button
              key={step}
              type="button"
              disabled={!enabled}
              className="rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-card)] px-3 py-1.5 text-sm font-semibold transition hover:border-emerald-500/60 disabled:opacity-30"
              onClick={() => adjustBakingTime(step)}
            >
              {step > 0 ? `+${step}s` : `${step}s`}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-2">
        <ProgressBar progress={timer.progress} overtime={timer.overtime} />
      </div>
    </StickyBanner>
  );
}

export default BakingTimerBanner;
